/**
 * Microservices Status Monitor
 * 
 * Monitors health status and SLA metrics for microservices
 */

import { APIClient } from './api-client';

export interface ServiceStatus {
  name: string;
  url: string;
  status: 'healthy' | 'unhealthy' | 'degraded' | 'unknown';
  responseTime?: number;
  uptime?: number;
  lastChecked?: string;
  version?: string;
  sla?: {
    target: number;
    current: number;
  };
  message?: string;
}

export interface StatusConfig {
  services: Array<{
    name: string;
    healthEndpoint: string;
    displayUrl?: string;
  }>;
  refreshInterval?: number;
}

export class StatusMonitor {
  private container: HTMLElement;
  private api: APIClient;
  private config: StatusConfig;
  private refreshTimer?: number;

  constructor(containerId: string, config: StatusConfig) {
    const container = document.getElementById(containerId);
    if (!container) {
      throw new Error(`Container with id "${containerId}" not found`);
    }

    this.container = container;
    this.api = new APIClient();
    this.config = config;
  }

  /**
   * Start monitoring services
   */
  public async start(): Promise<void> {
    await this.checkAllServices();

    if (this.config.refreshInterval) {
      this.refreshTimer = window.setInterval(() => {
        this.checkAllServices();
      }, this.config.refreshInterval);
    }
  }

  /**
   * Stop monitoring
   */
  public stop(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }
  }

  /**
   * Check all services
   */
  private async checkAllServices(): Promise<void> {
    const checks = this.config.services.map((service) => this.checkService(service));
    const results = await Promise.allSettled(checks);

    const statuses: ServiceStatus[] = results.map((result, index) => {
      if (result.status === 'fulfilled') {
        return result.value;
      } else {
        return {
          name: this.config.services[index].name,
          url: this.config.services[index].displayUrl || this.config.services[index].healthEndpoint,
          status: 'unhealthy',
          message: 'Failed to connect',
        };
      }
    });

    this.render(statuses);
  }

  /**
   * Check single service
   */
  private async checkService(service: {
    name: string;
    healthEndpoint: string;
    displayUrl?: string;
  }): Promise<ServiceStatus> {
    const startTime = Date.now();

    try {
      const response = await this.api.get(service.healthEndpoint);
      const responseTime = Date.now() - startTime;

      return {
        name: service.name,
        url: service.displayUrl || service.healthEndpoint,
        status: response.status === 'healthy' || response.status === 'ok' ? 'healthy' : 'degraded',
        responseTime,
        uptime: response.uptime,
        lastChecked: new Date().toISOString(),
        version: response.version,
        sla: response.sla,
        message: response.message,
      };
    } catch (error) {
      return {
        name: service.name,
        url: service.displayUrl || service.healthEndpoint,
        status: 'unhealthy',
        lastChecked: new Date().toISOString(),
        message: error instanceof Error ? error.message : 'Service unavailable',
      };
    }
  }

  /**
   * Render status dashboard
   */
  private render(statuses: ServiceStatus[]): void {
    const healthyCount = statuses.filter((s) => s.status === 'healthy').length;
    const totalCount = statuses.length;
    const overallHealth = (healthyCount / totalCount) * 100;

    const html = `
      <!-- Overall Status -->
      <div class="status-overview">
        <div class="status-metric">
          <div class="metric-value" style="color: ${overallHealth === 100 ? '#10b981' : overallHealth >= 70 ? '#f59e0b' : '#ef4444'};">
            ${healthyCount}/${totalCount}
          </div>
          <div class="metric-label">Services Healthy</div>
        </div>
        <div class="status-metric">
          <div class="metric-value">${overallHealth.toFixed(1)}%</div>
          <div class="metric-label">Overall Health</div>
        </div>
        <div class="status-metric">
          <div class="metric-value">${new Date().toLocaleTimeString()}</div>
          <div class="metric-label">Last Updated</div>
        </div>
      </div>

      <!-- Services Grid -->
      <div class="services-grid">
        ${statuses.map((service) => this.renderService(service)).join('')}
      </div>
    `;

    this.container.innerHTML = html;
  }

  /**
   * Render single service card
   */
  private renderService(service: ServiceStatus): string {
    const statusConfig = {
      healthy: { color: '#10b981', icon: '✓', label: 'Healthy' },
      unhealthy: { color: '#ef4444', icon: '✗', label: 'Unhealthy' },
      degraded: { color: '#f59e0b', icon: '⚠', label: 'Degraded' },
      unknown: { color: '#6b7280', icon: '?', label: 'Unknown' },
    };

    const config = statusConfig[service.status];

    return `
      <div class="service-card service-card--${service.status}">
        <div class="service-header">
          <div class="service-info">
            <div class="service-name">${service.name}</div>
            <div class="service-url">${service.url}</div>
          </div>
          <div class="service-status service-status--${service.status}">
            <span class="status-icon">${config.icon}</span>
            <span class="status-label">${config.label}</span>
          </div>
        </div>

        ${service.responseTime !== undefined || service.uptime !== undefined || service.version ? `
          <div class="service-metrics">
            ${service.responseTime !== undefined ? `
              <div class="metric-item">
                <span class="metric-icon">⚡</span>
                <span class="metric-text">${service.responseTime}ms</span>
              </div>
            ` : ''}
            ${service.uptime !== undefined ? `
              <div class="metric-item">
                <span class="metric-icon">⏱️</span>
                <span class="metric-text">${this.formatUptime(service.uptime)}</span>
              </div>
            ` : ''}
            ${service.version ? `
              <div class="metric-item">
                <span class="metric-icon">🏷️</span>
                <span class="metric-text">v${service.version}</span>
              </div>
            ` : ''}
          </div>
        ` : ''}

        ${service.sla ? `
          <div class="sla-meter">
            <div class="sla-label">
              <span>SLA: ${service.sla.current.toFixed(2)}%</span>
              <span class="sla-target">Target: ${service.sla.target}%</span>
            </div>
            <div class="sla-bar">
              <div class="sla-fill" style="width: ${service.sla.current}%; background: ${service.sla.current >= service.sla.target ? '#10b981' : '#ef4444'};"></div>
            </div>
          </div>
        ` : ''}

        ${service.message ? `
          <div class="service-message">${service.message}</div>
        ` : ''}
      </div>
    `;
  }

  /**
   * Format uptime seconds to readable string
   */
  private formatUptime(seconds: number): string {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    
    if (days > 0) {
      return `${days}d ${hours}h`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${Math.floor(seconds / 60)}m`;
    }
  }
}

